import logo from "./images/logo.png";
import floralDecoratorLogo from "./images/floral-decorator-logo.png";
import { getBranding } from "./theme/brandingStore";

/* Business presentation — logos and marks. The API never sends these yet:
   /group/stats returns ids, names and numbers, and everything visual is
   keyed back onto them here.

   A business in neither map falls back to a monogram, so a new tenant
   still gets a usable mark without a code change.

   Keys are normalised: lowercased with punctuation and spaces stripped, so
   "Ray's Florist", "Rays Florist" and "RAY'S FLORIST" all land on the same
   entry. Both the business name and its code are looked up, and aliases are
   cheap — add a key rather than letting a business fall through to its
   monogram. */
const LOGOS = {
  raysflorist: { src: logo, height: 26 }, // wide wordmark
  rf: { src: logo, height: 26 },
  thefloraldecorator: { src: floralDecoratorLogo, height: 34 },
  floraldecorator: { src: floralDecoratorLogo, height: 34 },
  fd: { src: floralDecoratorLogo, height: 34 },
};

/* Punctuation and spacing in business names is not dependable — the record
   may or may not carry the apostrophe, "The", or a trading suffix. */
const norm = (value) =>
  String(value || "")
    .toLowerCase()
    .replace(/[^a-z0-9]/g, "");

/** "Ray's Florist" -> "RF". Prefers the business code when there is one. */
function monogramOf(name, code) {
  if (code) return String(code).toUpperCase().slice(0, 3);
  const words = String(name || "")
    .replace(/[^\p{L}\p{N} ]/gu, "")
    .split(/\s+/)
    .filter(Boolean)
    .filter((w) => !/^(the|and|of)$/i.test(w));
  if (words.length === 0) return "??";
  return words
    .slice(0, 2)
    .map((w) => w[0].toUpperCase())
    .join("");
}

/**
 * Branding for one business.
 * `logo` is null when there is no artwork for it — render `monogram`
 * instead so a collapsed sidebar still shows something identifiable.
 *
 * An uploaded logo (Settings -> Branding, see src/theme/brandingStore.js)
 * wins, then the bundled artwork the branding doc names, then the
 * name/code lookup above. `tokens` is the business's colour palette.
 */
export function brandFor(business) {
  const { name, code } = business || {};
  const branding = getBranding(business);
  const logoDoc = branding.logo || {};
  const hit = LOGOS[norm(logoDoc.asset)] || LOGOS[norm(name)] || LOGOS[norm(code)];
  const src = logoDoc.url || (hit ? hit.src : null);
  return {
    logo: src,
    logoHeight: logoDoc.url ? null : hit ? hit.height : null,
    monogram: monogramOf(name, code),
    tokens: branding.tokens,
  };
}

/** The bundled artwork for a business, ignoring any upload. */
export function bundledLogoFor(business) {
  const { name, code } = business || {};
  const logoDoc = getBranding(business).logo || {};
  const hit = LOGOS[norm(logoDoc.asset)] || LOGOS[norm(name)] || LOGOS[norm(code)];
  return hit ? hit.src : null;
}

/* Businesses whose new work orders open on Retail rather than Funeral —
   The Floral Decorator (code FLDC) takes mostly retail orders. Keyed by
   business id, so a rename can't switch it off; the id is the one the open
   business is stored under (useActiveBusiness().id). */
const RETAIL_FIRST_IDS = [
  "6a914a0c209f265991ad914d", // The Floral Decorator
];

/** "Retail" or "Funeral" — which order type a new work order starts on. */
export function defaultOrderTypeFor(business) {
  const id = business && business.id;
  return id && RETAIL_FIRST_IDS.includes(String(id)) ? "Retail" : "Funeral";
}
